import { useCallback, useLayoutEffect, useRef } from 'react'

import { getAmountExcludingVat } from '@features/invoices/utils/getAmountExcludingVat'
import { getAmountIncludingVat } from '@features/invoices/utils/getAmountIncludingVat'
import { roundAmount } from '@features/reconciliation/utils/roundAmount'
import type { Account } from '@modules-deprecated/app/accounts/types'
import type { TaxRate } from '@modules-deprecated/app/taxrates/types'

import { useFormContext } from '../../../../../../../../../hooks'
import type { ReviewEntryDataFormSchema } from '../../../../../../../types/getReviewEntryDataFormSchema'
import { formatPaddedAccountNumber } from '../../../../../../../utils/formatPaddedAccountNumber'
import { getVatAccountNumberFromTaxRate } from '../../../../../../../utils/getVatAccountNumberFromTaxRate'

interface UseReviewEntryLineItemCalculationProps {
  accounts?: Account[]
}

export const useReviewEntryLineItemCalculation = ({ accounts = [] }: UseReviewEntryLineItemCalculationProps = {}) => {
  const { formState, getValues, setValue, trigger } = useFormContext<ReviewEntryDataFormSchema>()

  const accountsRef = useRef(accounts)
  const getValuesRef = useRef(getValues)
  const setValueRef = useRef(setValue)
  const triggerRef = useRef(trigger)
  const isSubmittedRef = useRef(formState.isSubmitted)

  useLayoutEffect(() => {
    accountsRef.current = accounts
    getValuesRef.current = getValues
    setValueRef.current = setValue
    triggerRef.current = trigger
    isSubmittedRef.current = formState.isSubmitted
  }, [accounts, formState.isSubmitted, getValues, setValue, trigger])

  const revalidateLine = useCallback((lineIndex: number) => {
    if (!isSubmittedRef.current) {
      return
    }
    triggerRef.current(`entryLines.${lineIndex}`).catch(() => undefined)
  }, [])

  const handleAccountChange = useCallback(
    (lineIndex: number, accountId: string | undefined, account: Account | undefined) => {
      setValueRef.current(`entryLines.${lineIndex}.accountId`, accountId ?? '')
      const resolvedAccount = account ?? accountsRef.current.find(({ id }) => id === accountId)
      setValueRef.current(
        `entryLines.${lineIndex}.accountNumber`,
        resolvedAccount?.accountNo != null ? formatPaddedAccountNumber(resolvedAccount.accountNo) : '',
      )
      setValueRef.current(`entryLines.${lineIndex}.accountName`, resolvedAccount?.name ?? '')
      revalidateLine(lineIndex)
    },
    [revalidateLine],
  )

  const handleVatRateChange = useCallback(
    (lineIndex: number, vatRateId: string | undefined, taxRate: TaxRate | undefined) => {
      setValueRef.current(`entryLines.${lineIndex}.vatRateId`, vatRateId ?? '')
      setValueRef.current(`entryLines.${lineIndex}.taxRate`, taxRate?.rate)

      const resolvedVatAccountNumber = getVatAccountNumberFromTaxRate(taxRate, accountsRef.current)
      const currentVatAccountNumber = getValuesRef.current(`entryLines.${lineIndex}.vatAccountNumber`)

      setValueRef.current(
        `entryLines.${lineIndex}.vatAccountNumber`,
        resolvedVatAccountNumber ?? (accountsRef.current.length === 0 ? (currentVatAccountNumber ?? null) : null),
      )

      if (taxRate?.rate === undefined) {
        revalidateLine(lineIndex)
        return
      }

      const currentAmountInclVat = getValuesRef.current(`entryLines.${lineIndex}.amountInclVat`)
      if (currentAmountInclVat !== undefined) {
        const newAmountExclVat = roundAmount(getAmountExcludingVat(currentAmountInclVat, taxRate.rate))
        setValueRef.current(`entryLines.${lineIndex}.amountExclVat`, newAmountExclVat)
        setValueRef.current(`entryLines.${lineIndex}.vatAmount`, roundAmount(currentAmountInclVat - newAmountExclVat))
        revalidateLine(lineIndex)
        return
      }

      const currentAmountExclVat = getValuesRef.current(`entryLines.${lineIndex}.amountExclVat`)
      if (currentAmountExclVat !== undefined) {
        const newAmountInclVat = roundAmount(getAmountIncludingVat(currentAmountExclVat, taxRate.rate))
        setValueRef.current(`entryLines.${lineIndex}.amountInclVat`, newAmountInclVat)
        setValueRef.current(`entryLines.${lineIndex}.vatAmount`, roundAmount(newAmountInclVat - currentAmountExclVat))
      }

      revalidateLine(lineIndex)
    },
    [revalidateLine],
  )

  const handleAmountExclVatChange = useCallback(
    (lineIndex: number, amountExclVat: number | undefined) => {
      setValueRef.current(`entryLines.${lineIndex}.amountExclVat`, amountExclVat)

      if (amountExclVat === undefined) {
        setValueRef.current(`entryLines.${lineIndex}.amountInclVat`, undefined)
        setValueRef.current(`entryLines.${lineIndex}.vatAmount`, undefined)
        revalidateLine(lineIndex)
        return
      }

      const taxRate = getValuesRef.current(`entryLines.${lineIndex}.taxRate`)
      if (taxRate === undefined) {
        setValueRef.current(`entryLines.${lineIndex}.amountInclVat`, undefined)
        setValueRef.current(`entryLines.${lineIndex}.vatAmount`, undefined)
        revalidateLine(lineIndex)
        return
      }

      const amountInclVat = roundAmount(getAmountIncludingVat(amountExclVat, taxRate))
      const vatAmount = roundAmount(amountInclVat - amountExclVat)

      setValueRef.current(`entryLines.${lineIndex}.amountInclVat`, amountInclVat)
      setValueRef.current(`entryLines.${lineIndex}.vatAmount`, vatAmount)
      revalidateLine(lineIndex)
    },
    [revalidateLine],
  )

  const handleAmountInclVatChange = useCallback(
    (lineIndex: number, amountInclVat: number | undefined) => {
      setValueRef.current(`entryLines.${lineIndex}.amountInclVat`, amountInclVat)

      if (amountInclVat === undefined) {
        setValueRef.current(`entryLines.${lineIndex}.amountExclVat`, undefined)
        setValueRef.current(`entryLines.${lineIndex}.vatAmount`, undefined)
        revalidateLine(lineIndex)
        return
      }

      const taxRate = getValuesRef.current(`entryLines.${lineIndex}.taxRate`)
      if (taxRate === undefined) {
        setValueRef.current(`entryLines.${lineIndex}.amountExclVat`, undefined)
        setValueRef.current(`entryLines.${lineIndex}.vatAmount`, undefined)
        revalidateLine(lineIndex)
        return
      }

      const amountExclVat = roundAmount(getAmountExcludingVat(amountInclVat, taxRate))
      const vatAmount = roundAmount(amountInclVat - amountExclVat)

      setValueRef.current(`entryLines.${lineIndex}.amountExclVat`, amountExclVat)
      setValueRef.current(`entryLines.${lineIndex}.vatAmount`, vatAmount)
      revalidateLine(lineIndex)
    },
    [revalidateLine],
  )

  return {
    handleAccountChange,
    handleAmountExclVatChange,
    handleAmountInclVatChange,
    handleVatRateChange,
  }
}
